// 태그 칸의 '=' 수식 — 칸을 벗어날 때(Enter·Tab·클릭) 한 번 계산해 값으로 바꾼다 (명세에는 값만 남는다).
// 계산이 안 되면 수식 글자를 그대로 두고 칸을 붉게 표시한다 (고쳐서 다시 Enter).
//
//   =40001-40000            사칙연산 (+ - * /, 괄호)               → 1
//   =↑+1  =UP+1             위 칸(같은 열) 값에 더하기             → 위가 1-3x4 면 1-3x5 (글자 속 마지막 숫자를 더함)
//   =[설명]                 같은 행 다른 칸 / 디바이스 칸           → [주소] [크기] [Alias] … [설비구분] [제조사] [모델] [디바이스]
//   ="110_"&[설비구분]&"_PV" 글자 잇기 (&)
//   ="110_CY"&ROW&"_PV"     ROW = 이 표의 행 번호(1부터)
//   ={code}_PV               {이름} = 디바이스·같은 행 칸 값. 수식으로 안 풀리면 글자 틀(템플릿)로 보고 {…} 자리만 채운다
//   ={code}&"_"&ROW           → CNC01_3 (수식 안에서도 {…} 를 값처럼 쓴다)
//
// 연산 칸은 LiveEdge 의 값 변환식(X*0.1 등) 자리라 수식으로 다루지 않는다.

import type { DeviceSpecTag } from '../../types'

// 수식이 참조하는 태그 칸 (수식 원문 칸 formulas 는 빼고 글자 칸만)
type TagText = Exclude<keyof DeviceSpecTag, 'formulas'>

export interface FormulaContext {
  row: DeviceSpecTag
  prev: DeviceSpecTag | undefined // 위 행 (첫 행이면 없음)
  rowIndex: number // 0부터
  field: TagText
  device: { category: string; maker: string; model: string; name: string; code: string }
}

export type FormulaResult = { ok: true; value: string } | { ok: false; error: string }

const TAG_REFS: Record<string, TagText> = {
  '주소': 'address', '크기': 'size', '타입': 'dataType', '연산': 'arithmetic', '설명': 'description',
  'alias': 'alias', '별칭': 'alias',
  'meskey': 'alias', 'mes키': 'alias', // 옛 이름 MesKey — 저장된 [MesKey] 수식이 계속 동작하도록 남긴다
}
const DEVICE_REFS: Record<string, keyof FormulaContext['device']> = {
  '설비구분': 'category', '제조사': 'maker', '모델': 'model', '디바이스': 'name', '디바이스이름': 'name', '이름': 'name',
  'category': 'category', 'maker': 'maker', 'model': 'model', 'device': 'name', 'name': 'name', '디바이스코드': 'code', 'code': 'code',
}
const TAG_REF_ALIASES: Record<string, TagText> = {
  'address': 'address', 'size': 'size', 'type': 'dataType', 'datatype': 'dataType', 'arithmetic': 'arithmetic', 'description': 'description',
}

export const isFormula = (value: string) => value.trimStart().startsWith('=')

type Value = number | string

class FormulaError extends Error {}

const NUMBER = /^-?\d+(\.\d+)?$/
const asNumber = (v: Value): number | null => (typeof v === 'number' ? v : NUMBER.test(v.trim()) ? Number(v) : null)
const format = (v: Value) => (typeof v === 'number' ? String(Number(v.toFixed(10))) : v)

// "1-3x4" + 1 → "1-3x5" — 글자 속 마지막 숫자에 더한다 (채우기 핸들과 같은 감각)
function shiftLastNumber(text: string, delta: number): string {
  const m = /^(.*?)(\d+)(\D*)$/.exec(text)
  if (!m) throw new FormulaError(`'${text}' 에 더할 숫자가 없습니다`)
  const next = Number(m[2]) + delta
  if (next < 0) throw new FormulaError('결과가 음수입니다')
  const digits = m[2].length > 1 && m[2].startsWith('0') ? String(next).padStart(m[2].length, '0') : String(next)
  return m[1] + digits + m[3]
}

function add(a: Value, b: Value, sign: 1 | -1): Value {
  const na = asNumber(a), nb = asNumber(b)
  if (na != null && nb != null) return na + sign * nb
  if (nb != null && typeof a === 'string') return shiftLastNumber(a, sign * nb)
  if (na != null && typeof b === 'string' && sign === 1) return shiftLastNumber(b, na)
  throw new FormulaError(`'${format(a)}' ${sign === 1 ? '+' : '-'} '${format(b)}' 를 계산할 수 없습니다 (글자 잇기는 &)`)
}

function multiply(a: Value, b: Value, op: '*' | '/'): number {
  const na = asNumber(a), nb = asNumber(b)
  if (na == null || nb == null) throw new FormulaError(`'${op}' 는 숫자끼리만 됩니다`)
  if (op === '/' && nb === 0) throw new FormulaError('0 으로 나눌 수 없습니다')
  return op === '*' ? na * nb : na / nb
}

// 재귀 하강: concat := sum ('&' sum)* · sum := product (('+'|'-') product)* · product := unary (('*'|'/') unary)*
function parse(src: string, ctx: FormulaContext): Value {
  let i = 0
  const peek = () => { while (src[i] === ' ') i++; return src[i] }
  const eat = (ch: string) => { if (peek() === ch) { i++; return true } return false }

  const primary = (): Value => {
    const c = peek()
    if (c === undefined) throw new FormulaError('수식이 끝나지 않았습니다')
    if (c === '(') {
      i++
      const v = concat()
      if (!eat(')')) throw new FormulaError("')' 가 없습니다")
      return v
    }
    if (c === '"') {
      const end = src.indexOf('"', i + 1)
      if (end < 0) throw new FormulaError('따옴표가 닫히지 않았습니다')
      const s = src.slice(i + 1, end)
      i = end + 1
      return s
    }
    if (c === '[' || c === '{') {
      const close = c === '[' ? ']' : '}'
      const end = src.indexOf(close, i + 1)
      if (end < 0) throw new FormulaError(`'${close}' 가 없습니다`)
      const raw = src.slice(i + 1, end)
      i = end + 1
      return lookup(raw, ctx)
    }
    if (c === '↑' || /^up\b/i.test(src.slice(i))) {
      i += c === '↑' ? 1 : 2
      if (!ctx.prev) throw new FormulaError('첫 행이라 위 칸이 없습니다')
      const v = ctx.prev[ctx.field] ?? ''
      if (isFormula(v)) throw new FormulaError('위 칸도 계산되지 않은 수식입니다')
      return v
    }
    if (/^row\b/i.test(src.slice(i))) { i += 3; return ctx.rowIndex + 1 }
    const num = /^\d+(\.\d+)?/.exec(src.slice(i))
    if (num) { i += num[0].length; return Number(num[0]) }
    throw new FormulaError(`'${src.slice(i, i + 8)}' 를 해석할 수 없습니다`)
  }

  const unary = (): Value => {
    if (eat('-')) {
      const v = asNumber(unary())
      if (v == null) throw new FormulaError("'-' 뒤에는 숫자가 와야 합니다")
      return -v
    }
    return primary()
  }

  const product = (): Value => {
    let v = unary()
    for (let c = peek(); c === '*' || c === '/'; c = peek()) { i++; v = multiply(v, unary(), c) }
    return v
  }

  const sum = (): Value => {
    let v = product()
    for (let c = peek(); c === '+' || c === '-'; c = peek()) { i++; v = add(v, product(), c === '+' ? 1 : -1) }
    return v
  }

  const concat = (): Value => {
    let v = sum()
    while (eat('&')) v = format(v) + format(sum())
    return v
  }

  const result = concat()
  if (peek() !== undefined) throw new FormulaError(`'${src.slice(i, i + 8)}' 부분을 해석할 수 없습니다`)
  return result
}

// [이름] / {이름} — 같은 행 태그 칸 또는 디바이스 칸 (대소문자·공백 무시), ROW 는 행 번호
// 이름 정규화 — 대소문자·공백·_·- 무시. 없으면 철자가 1~2글자 다른 가장 가까운 이름 (interFacdID → interfaceid)
const normalizeName = (raw: string) => raw.replace(/[\s_-]+/g, '').toLowerCase()

function distance(a: string, b: string): number {
  const dp = Array.from({ length: b.length + 1 }, (_, j) => j)
  for (let i = 1; i <= a.length; i++) {
    let prev = dp[0]
    dp[0] = i
    for (let j = 1; j <= b.length; j++) {
      const tmp = dp[j]
      dp[j] = Math.min(dp[j] + 1, dp[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1))
      prev = tmp
    }
  }
  return dp[b.length]
}

const ALL_NAMES = () => ['row', ...Object.keys(TAG_REFS), ...Object.keys(TAG_REF_ALIASES), ...Object.keys(DEVICE_REFS)]

export function resolveName(raw: string): string | null {
  const name = normalizeName(raw)
  const names = ALL_NAMES()
  if (names.includes(name)) return name
  // 짧은 이름은 오타 허용하지 않는다 (if → id 같은 오인 방지)
  if (name.length < 5) return null
  let best: string | null = null, bestD = 3
  for (const n of names) {
    if (n.length < 5) continue
    const d = distance(name, n)
    if (d < bestD || (d === bestD && best && n.length < best.length)) { best = n; bestD = d }
  }
  return bestD <= (name.length >= 8 ? 2 : 1) ? best : null
}

function lookup(raw: string, ctx: FormulaContext): Value {
  const name = resolveName(raw) ?? normalizeName(raw)
  if (name === 'row') return ctx.rowIndex + 1
  const tagField = TAG_REFS[name] ?? TAG_REF_ALIASES[name]
  if (tagField) {
    if (tagField === ctx.field) throw new FormulaError('자기 칸을 참조할 수 없습니다')
    const v = ctx.row[tagField] ?? ''
    if (isFormula(v)) throw new FormulaError(`${raw} 칸도 계산되지 않은 수식입니다`)
    return v
  }
  const deviceField = DEVICE_REFS[name]
  if (deviceField) {
    const v = ctx.device[deviceField] ?? ''
    if (!v) throw new FormulaError(`디바이스의 ${raw} 이(가) 비어 있습니다`)
    return v
  }
  throw new FormulaError(`알 수 없는 이름 ${raw} — 설비구분·제조사·모델·디바이스·주소·크기·설명·Alias(별칭)·ROW`)
}

// 글자 틀 — {이름} 자리만 값으로 바꾼다 (={code}_PV → CNC01_PV)
function template(src: string, ctx: FormulaContext): string {
  return src.replace(/\{([^{}]*)\}/g, (_, raw: string) => format(lookup(raw, ctx)))
}

export function evaluateFormula(text: string, ctx: FormulaContext): FormulaResult {
  const src = text.trimStart().slice(1).trim()
  if (!src) return { ok: false, error: '수식이 비어 있습니다' }
  try {
    return { ok: true, value: format(parse(src, ctx)) }
  } catch (e) {
    // 수식으로 안 풀리지만 {…} 가 있으면 글자 틀로 — 이름이 틀렸으면 그 오류를 보여 준다
    if (src.includes('{')) {
      try {
        return { ok: true, value: template(src, ctx) }
      } catch (te) {
        return { ok: false, error: te instanceof FormulaError ? te.message : '계산할 수 없습니다' }
      }
    }
    return { ok: false, error: e instanceof FormulaError ? e.message : '계산할 수 없습니다' }
  }
}
